import { useEffect, useState } from 'react';
import {
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    useTheme,
} from '@mui/material';
import AddressInput from '@/components/ui/AddressInput/AddressInput';
import Box from '@/components/ui/Box/Box';
import Button from '@/components/ui/Button/Button';
import FieldWrapper from '@/components/ui/FieldWrapper/FieldWrapper';
import TextField from '@/components/ui/TextField/TextField';
import Typography from '@/components/ui/Typography/Typography';
import type { ParsedAddress } from '@/helpers/addressParser';
import type { LocationDetailsFormValues } from '../LocationDetails/ILocationDetails';
import type { NewLocationModalProps } from './INewLocationModal';
import { styles } from './styles';

const EMPTY: LocationDetailsFormValues = {
    nickname: '',
    formatted_address: '',
    street_number: '',
    street_name: '',
    subpremise: '',
    city: '',
    state: '',
    zip: '',
    country: '',
    contact_name: '',
    contact_email: '',
    contact_phone: '',
    notes: '',
};

const NewLocationModal = ({ open, onClose, onCreate }: NewLocationModalProps) => {
    const theme = useTheme();
    const [form, setForm] = useState<LocationDetailsFormValues>(EMPTY);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (open) setForm(EMPTY);
    }, [open]);

    const isValid = Boolean(form.nickname.trim() && form.formatted_address.trim());

    const handleSelectAddress = (place: ParsedAddress) => {
        setForm((prev) => ({
            ...prev,
            formatted_address: place.formatted_address ?? '',
            street_number: place.street_number ?? '',
            street_name: place.street_name ?? '',
            city: place.city ?? '',
            state: place.state ?? '',
            zip: place.zip ?? '',
            country: place.country ?? '',
            place_id: place.place_id ?? prev.place_id,
        }));
    };

    const handleSubmit = async () => {
        if (!isValid) return;
        setSubmitting(true);
        try {
            await onCreate(form);
            onClose();
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth sx={styles.dialog}>
            <DialogTitle sx={styles.title}>Add New Location</DialogTitle>
            <DialogContent sx={styles.content}>
                <FieldWrapper filled={Boolean(form.nickname)} required>
                    <TextField
                        fullWidth
                        label="Location Name"
                        value={form.nickname}
                        required
                        onChange={(e) =>
                            setForm({ ...form, nickname: e.target.value })
                        }
                    />
                </FieldWrapper>
                <FieldWrapper filled={Boolean(form.formatted_address)} required>
                    <AddressInput
                        fullWidth
                        label="Address"
                        value={form.formatted_address}
                        required
                        onChange={(value) =>
                            setForm({ ...form, formatted_address: value })
                        }
                        onSelect={handleSelectAddress}
                    />
                </FieldWrapper>
                <FieldWrapper filled={Boolean(form.subpremise)}>
                    <TextField
                        fullWidth
                        label="Address 2"
                        value={form.subpremise}
                        onChange={(e) =>
                            setForm({ ...form, subpremise: e.target.value })
                        }
                    />
                </FieldWrapper>
                <FieldWrapper filled={Boolean(form.contact_name)}>
                    <TextField
                        fullWidth
                        label="Contact Name"
                        value={form.contact_name}
                        onChange={(e) =>
                            setForm({ ...form, contact_name: e.target.value })
                        }
                    />
                </FieldWrapper>
                <FieldWrapper filled={Boolean(form.contact_email)}>
                    <TextField
                        fullWidth
                        label="Contact Email"
                        type="email"
                        value={form.contact_email}
                        onChange={(e) =>
                            setForm({ ...form, contact_email: e.target.value })
                        }
                    />
                </FieldWrapper>
                <FieldWrapper filled={Boolean(form.contact_phone)}>
                    <TextField
                        fullWidth
                        label="Contact Phone"
                        value={form.contact_phone}
                        onChange={(e) =>
                            setForm({ ...form, contact_phone: e.target.value })
                        }
                    />
                </FieldWrapper>
                <Box>
                    <FieldWrapper filled={Boolean(form.notes)}>
                        <TextField
                            fullWidth
                            label="Notes"
                            multiline
                            minRows={2}
                            value={form.notes}
                            onChange={(e) =>
                                setForm({ ...form, notes: e.target.value })
                            }
                        />
                    </FieldWrapper>
                    <Typography sx={{ fontSize: '12px', color: 'grey.500', mt: 0.5 }}>
                        Notes will be visible to repairers
                    </Typography>
                </Box>
            </DialogContent>
            <DialogActions sx={styles.actions}>
                <Button
                    variant="contained"
                    sx={styles.submitBtn(theme)}
                    disabled={!isValid || submitting}
                    onClick={handleSubmit}
                >
                    {submitting ? 'Creating…' : 'Create Location'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default NewLocationModal;
